import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation, translations } from '../translations';
import { getInventoryCategoriesStorageKey, getInventorySettingsStorageKey } from '../utils/inventoryStorage';
import { baseRequiredFields, defaultRequiredFields, baseFormFields, defaultVisibleColumns } from '../utils/inventoryFieldDefs';
import useBarcodeScan from '../utils/useBarcodeScan';
import ScanSources from '../components/scanner/ScanSources';
import { API_BASE } from '../config';
import { Banner } from '../components/ui/Banner';
import { IconButton, panelTopHairline } from '../ui/common';
import { scrollToTop } from '../utils/scrollToTop';
import { ArrowLeftIcon, XIcon } from '../ui/icons';
import { typeLabel } from '../utils/dictionaryLabel';
import { localeTag } from '../utils/format';
import { kolejneNumery, skrotSerii, iloscZPola, MAKS_SZTUK } from '../utils/kolejneNumery';
import { zapiszZPrzeniesieniemNumeru } from '../utils/przeniesienieNumeru';
import { useConfirm } from '../components/ui/Modal';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// E.7: kanon wymagalności przyjechał z `utils/inventoryFieldDefs.js` — do E.7 ta lista
// była tu ZDUPLIKOWANA (trzecie źródło prawdy, odnotowane przy E.5) i rozjechałaby się
// przy pierwszej zmianie kanonu. `baseRequiredFields` = pola NOT NULL bez DEFAULT-a
// (nie do odznaczenia), `defaultRequiredFields` = stan startowy org bez konfiguracji.

const normalizeActiveFieldKey = (key) => {
  const k = String(key || '').trim();
  if (!k) return '';

  const map = {
    class: 'classId',
    class_id: 'classId',
    type: 'typeId',
    type_id: 'typeId',
    category: 'typeId',
    inventoryNumber: 'inventory_number',
    purchase_date: 'purchaseDate',
    maintenance_date: 'maintenanceDate',
    wear_level: 'wearLevel',
    size_range: 'sizeRange',
    priceCurrency: 'currency',
    depositCurrency: 'currency',
    price_currency: 'currency',
    deposit_currency: 'currency'
  };

  return map[k] || k;
};

const loadSettings = () => {
  if (typeof window === 'undefined') {
    return { requiredFields: defaultRequiredFields, categoryRequirements: {}, categories: [], customColumns: [], visibleColumns: defaultVisibleColumns };
  }
  try {
    const settingsKey = getInventorySettingsStorageKey();
    const categoriesKey = getInventoryCategoriesStorageKey();
    const raw = window.localStorage.getItem(settingsKey);
    const parsed = raw ? JSON.parse(raw) : {};
    const fromStorage = Array.isArray(parsed?.requiredFields) && parsed.requiredFields.length > 0
      ? parsed.requiredFields
      : defaultRequiredFields;
    const requiredFields = Array.from(new Set([...(fromStorage || []), ...baseRequiredFields]));
    const categoryRequirements = parsed?.categoryRequirements || {};
    const rawCategories = parsed?.categories || window.localStorage.getItem(categoriesKey);
    const parsedCategories = Array.isArray(rawCategories)
      ? rawCategories
      : (() => {
          try {
            return rawCategories ? JSON.parse(rawCategories) : [];
          } catch {
            return [];
          }
        })();
    const categories = Array.from(new Set([
      ...(Array.isArray(parsedCategories) ? parsedCategories : []),
      ...Object.keys(categoryRequirements || {})
    ].map(v => String(v || '').trim()).filter(Boolean)));
    // Kolumny własne (E.2) — do 2026-09-23 ten formularz o nich NIE WIEDZIAŁ: `metadata`
    // budowało się ze sztywnej listy kluczy wbudowanych, a każde pole miało ręcznie
    // napisany blok JSX. Firma mogła więc zdefiniować własną kolumnę, zobaczyć ją
    // w tabeli magazynu i wypełnić importem — ale nie dało się jej wpisać ręcznie.
    const customColumns = (Array.isArray(parsed?.customColumns) ? parsed.customColumns : [])
      .filter(c => c && c.key);
    // Kolumny pokazywane w tabeli magazynu: Rozmiar i Klasę formularz oferuje, gdy firma je
    // pokazuje (24.10.2026). Bez tego kolumna była w tabeli, ale nie dało się jej wypełnić.
    const visibleColumns = Array.isArray(parsed?.visibleColumns) && parsed.visibleColumns.length > 0
      ? parsed.visibleColumns
      : defaultVisibleColumns;
    return { requiredFields, categoryRequirements, categories, customColumns, visibleColumns };
  } catch {
    return { requiredFields: defaultRequiredFields, categoryRequirements: {}, categories: [], customColumns: [], visibleColumns: defaultVisibleColumns };
  }
};

// SVG Icons
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);


const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DotIcon = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="4" cy="4" r="3" fill="currentColor"/>
  </svg>
);

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  max-width: 920px; /* zwiększone */
  margin: 32px auto;
  padding: 40px;
  padding-bottom: 64px; /* więcej miejsca na dole */
  min-height: calc(100vh - 450px); /* większy panel na dużych ekranach */
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  overflow: visible; /* pozwala dropdownowi wychodzić poza panel */
  animation: ${slideIn} 0.6s ease-out;
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 32px);
  min-height: calc(100vh - 48px);

  ${panelTopHairline}

  @media (min-width: 1200px) {
    max-width: 1000px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const TitleSection = styled.div`
  flex: 1;
  text-align: center;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  font-size: 2rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

`;

const TitleIcon = styled.div`
  color: var(--primary);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--text-muted);
  font-weight: 400;
`;

const FormPanel = styled.div`
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 32px;
  box-sizing: border-box;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  
  @media(max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormRowFull = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/* Podpowiedz o czytniku pod polem kodu. Bez niej nikt sie nie domysli, ze skanowanie
   dziala — czytnik nie ma zadnego interfejsu, wiec jedyne, co o nim mowi, to ten napis.
   Po udanym skanie ta sama linijka zmienia sie w potwierdzenie, zamiast dokladac druga. */
const ScanHint = styled.p`
  margin: 6px 0 0;
  font-size: 0.78125rem;
  line-height: 1.4;
  color: ${({ $done }) => ($done ? 'var(--success)' : 'var(--text-muted)')};
`;

// Liczba sztuk (24.10.2026): licznik z przyciskami i podgląd numerów, które dostaną sztuki.
const QuantityPanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
  padding: 18px 20px;
  border-radius: 14px;
  border: 1px solid var(--input-border);
  background: var(--table-header-bg);
`;

const QuantityField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: stretch;
  height: 50px;
  border: 2px solid ${p => (p.$error ? 'var(--danger)' : 'var(--input-border)')};
  border-radius: 12px;
  overflow: hidden;
  background: var(--input-bg);

  &:focus-within {
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

const StepButton = styled.button`
  width: 46px;
  border: 0;
  background: transparent;
  color: var(--text);
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) { color: var(--primary); background: var(--table-row-hover); }
  &:disabled { color: var(--text-muted); opacity: 0.45; cursor: default; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: -3px; }
`;

const StepInput = styled.input`
  width: 64px;
  border: 0;
  border-left: 1px solid var(--input-border);
  border-right: 1px solid var(--input-border);
  background: transparent;
  color: var(--text);
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  outline: none;
`;

const QuantityInfo = styled.div`
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--text-muted);

  strong { color: var(--text); font-weight: 600; }
`;

const SeriesPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;

  code {
    font-family: 'Courier New', monospace;
    font-weight: 700;
    font-size: 0.85rem;
    color: var(--text);
    padding: 3px 8px;
    border-radius: 6px;
    border: 1px solid var(--input-border);
    background: var(--card-bg);
  }

  span { color: var(--text-muted); }
`;

const Label = styled.label`
  font-size: 0.95rem;
  color: var(--text);
  font-weight: 600;
  letter-spacing: 0.025em;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid ${p => (p['data-error'] ? 'var(--danger)' : 'var(--input-border)')};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: ${p => (p['data-error'] ? 'var(--danger-bg)' : 'var(--input-bg)')};
  transition: all 0.2s ease;
  box-sizing: border-box;
  color: var(--text);

  &:focus {
    outline: none;
    border-color: ${p => (p['data-error'] ? 'var(--danger)' : 'var(--primary)')};
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }

  &[type='date'][value=''] {
    color: transparent;
  }

  &[type='date'][value='']::-webkit-datetime-edit {
    color: transparent;
  }

  &[type='date']::-webkit-calendar-picker-indicator {
    opacity: 1;
    cursor: pointer;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

const TypeaheadWrapper = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const TypeaheadList = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: var(--z-dropdown);
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  max-height: 240px;
  overflow-y: auto;
  padding: 6px;
`;

const TypeaheadItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(59,130,246,0.08);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 32px;
  
  @media(max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  /* 210px, a nie 140px: napis zmienia sie w trakcie zapisu („Dodaj sprzet" -> „Zapisywanie..."),
     a przy ciasnym min-width przycisk ROSNIE i wraca, wiec skacze pod kursorem dokladnie w chwili
     klikniecia. Szerokosc dobrana tak, zeby zmiescil sie NAJDLUZSZY wariant we wszystkich trzech
     jezykach — stad tez skrocone „Zapisywanie..." zamiast „Trwa zapisywanie..." w slowniku.
     Zglosil Piotrek 2026-08-08 (V.6). */
  min-width: 210px;
`;

const SecondaryButton = styled.button`
  background: var(--input-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  padding: 12px 32px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  /* Ta sama szerokosc co PrimaryButton (210px) -- obok siebie w ButtonRow maja wygladac jak
     jedna dopasowana para, nie jeden dominujacy przycisk i jeden przypadkowo mniejszy. */
  min-width: 210px;

  &:hover {
    background: var(--table-row-hover);
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-1px);
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
  width: ${props => props.compact ? '360px' : '100%'}; /* zmniejszone z 480px -> 360px */
`;

// skrócony padding, mniejszy font i ellipsis dla wybranej wartości
const FilterDropdownButton = styled.button`
  width: 100%;
  padding: 12px 12px;
  border: 2px solid ${p => (p['data-error'] ? 'var(--danger)' : 'var(--input-border)')};
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 500;
  background: ${p => (p['data-error'] ? 'var(--danger-bg)' : 'var(--input-bg)')};
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.2;
  transition: border-color .2s, box-shadow .2s, background .2s;
  gap: 8px;
  
  /* większy rozmiar dla dopasowania do pola Deposit (użyj data-size="large") */
  &[data-size="large"] {
    padding: 14px 16px;
    font-size: 1rem;
  }

  &:hover { border-color: var(--primary); }
  &:focus-visible {
    outline: none;
    border-color: ${p => (p['data-error'] ? 'var(--danger)' : 'var(--primary)')};
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  &[data-open="true"] {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
    svg { color: var(--primary); }
  }
  &[data-placeholder="true"] {
    span:first-child { color: var(--text-muted); font-weight: 400; }
  }

  span:first-child {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  svg { flex-shrink: 0; transition: color .2s; color: var(--text-muted); }
`;

const FilterOptionsList = styled.ul`
  position: absolute;
  left: 0;
  width: 100%;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  box-sizing: border-box;
  z-index: var(--z-popover);
`;
 
/* DODANE: brakujący komponent stylowany dla opcji */
const FilterOptionItem = styled.li`
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background .15s, color .15s;
  display: block;

  .option-content {
    display: flex;
    align-items: center;
    gap: 8px;
    svg { color: var(--primary); flex-shrink: 0; }
    .option-label {
      flex: 1;
      white-space: normal; /* pozwala zawijać tekst */
      overflow: visible;
      text-overflow: clip;
    }
  }

  &:hover { background: var(--table-row-hover); }

  &[data-selected="true"] {
    background: rgba(59,130,246,0.15);
    color: var(--primary);
    font-weight: 600;
  }
`;

function FilterDropdown({ value, onChange, options, placeholder, compact, size, preferredHeight, error, dataCy }) {
  const [open, setOpen] = React.useState(false);
  const [openUp, setOpenUp] = React.useState(false);
  const [listMaxHeight, setListMaxHeight] = React.useState(160);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef(null);

  const close = () => setOpen(false);

  const recalcPosition = React.useCallback(() => {
    if (!ref.current) return;
    const btn = ref.current.querySelector('button');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const margin = 20; // odstęp od krawędzi okna
    const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - margin);
    const spaceAbove = Math.max(0, rect.top - margin);

    const preferred = preferredHeight || 260; // preferowana wysokość listy (można nadpisać propem)
    let useUp = false;
    let maxH = Math.min(preferred, spaceBelow);
    if (spaceBelow < preferred && spaceAbove > spaceBelow) {
      useUp = true;
      maxH = Math.min(preferred, spaceAbove);
    }
    // minimalna i maksymalna dopuszczalna wysokość
    maxH = Math.max(100, Math.min(maxH, 520));
    setOpenUp(useUp);
    setListMaxHeight(maxH);
  }, [preferredHeight]);

  React.useEffect(() => {
    if (!open) return;
    recalcPosition();
    const onResize = () => recalcPosition();
    window.addEventListener('resize', onResize);
    document.addEventListener('scroll', recalcPosition, true);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scroll', recalcPosition, true);
    };
  }, [open, recalcPosition]);

  React.useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const selected = options.find(o => o.value === value);
  const filteredOptions = options.filter(o => {
    if (!query) return true;
    return String(o.label || '').toLowerCase().includes(String(query).toLowerCase());
  });

  return (
    <DropdownWrapper ref={ref} compact={compact}>
      <FilterDropdownButton
        type="button"
        data-open={open}
        data-placeholder={!selected}
        data-size={size || 'default'}
        data-error={!!error}
        data-cy={dataCy}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!open) recalcPosition();
          if (!open) setQuery('');
          setOpen(o => !o);
        }}
      >
        <span>{selected ? selected.label : placeholder}</span>
        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </FilterDropdownButton>

      {open && (
        <FilterOptionsList
          role="listbox"
          style={{
            top: openUp ? 'auto' : 'calc(100% + 6px)',
            bottom: openUp ? 'calc(100% + 6px)' : 'auto',
            maxHeight: `${listMaxHeight}px`
          }}
        >
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--input-border)' }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Wpisz, aby filtrować…"
              autoFocus
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid var(--input-border)',
                borderRadius: 8,
                background: 'var(--input-bg)',
                color: 'var(--text)'
              }}
            />
          </div>
          {filteredOptions.map(o => {
            const sel = o.value === value;
            return (
              <FilterOptionItem
                key={o.value || '__' + o.label}
                data-selected={sel}
                onClick={() => { onChange(o.value); close(); }}
              >
                <span className="option-content">
                  {sel && <DotIcon />}
                  <span className="option-label">{o.label}</span>
                </span>
              </FilterOptionItem>
            );
          })}
          {filteredOptions.length === 0 && (
            <div style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>Brak dopasowań</div>
          )}
        </FilterOptionsList>
      )}
    </DropdownWrapper>
  );
}

export default function AddEquipment() {
  const TYPEAHEAD_MIN_LENGTH = 2;
  const initialSettings = loadSettings();
  const [form, setForm] = useState({
    name: '',
    size: '',
    brand: '',
    price: '',
    price_over_7: '',
    bail: '',
    inventory_number: '', // <-- dodane pole
    classId: '',
    typeId: '',
    category: '',   // <-- dodane pole category
    sku: '',
    condition: '',
    location: '',
    tags: '',
    supplier: '',
    purchaseDate: '',
    maintenanceDate: '',
    images: '',
    barcode: '',
    currency: '',
    wearLevel: '',
    compatibility: '',
    sizeRange: ''
  });
  const [classes, setClasses] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState(initialSettings.categories || []);
   const [error, setError] = useState('');
   const [fieldErrors, setFieldErrors] = useState({});
   const [success, setSuccess] = useState('');
   const [loading, setLoading] = useState(false);
  const purchaseDateRef = useRef(null);
  const maintenanceDateRef = useRef(null);
  const [requiredFields, setRequiredFields] = useState(initialSettings.requiredFields);
  const [visibleColumns, setVisibleColumns] = useState(initialSettings.visibleColumns);
  // Definicje kolumn własnych i wpisane w nie wartości. Wartości trzymamy OSOBNO od `form`,
  // bo `form` jest przeglądane po kluczach (licznik aktywnych pól, walidacja), a klucze pól
  // własnych pochodzą od użytkownika i nie mogą się tam mieszać z kolumnami bazowymi.
  const [customColumns, setCustomColumns] = useState(initialSettings.customColumns || []);
  const [customValues, setCustomValues] = useState({});
  // Pole, które na tej stronie jest „polem kodu”. Naklejka na sprzęcie niesie albo kod
  // kreskowy/QR, albo numer inwentarzowy — więc celujemy w to pierwsze, a gdy organizacja
  // go nie używa, w drugie. Gdy nie ma żadnego, nasłuch czytnika w ogóle się nie włącza:
  // kod nie miałby gdzie trafić, a wpisanie go „gdziekolwiek” byłoby gorsze niż nic.
  const [scannedInto, setScannedInto] = useState(null);
  const [categoryRequirements, setCategoryRequirements] = useState(initialSettings.categoryRequirements);
  // Currency autocomplete state
  const [currencies, setCurrencies] = useState([]);
  const [currencySuggestions, setCurrencySuggestions] = useState([]);
  const [showCurrencySuggestions, setShowCurrencySuggestions] = useState(false);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const currencyContainerRef = useRef(null);
  const [fieldSuggestionIndex, setFieldSuggestionIndex] = useState({});
  const [activeTypeahead, setActiveTypeahead] = useState({ field: '', items: [] });
  // fetch available currencies (code, name, flag) from a free public API
  const fetchCurrencies = async () => {
    if (currencies.length || currenciesLoading) return;
    setCurrenciesLoading(true);
    try {
      const res = await axios.get('https://restcountries.com/v3.1/all?fields=flags,currencies');
      const rows = res.data || [];
      const map = {};
      rows.forEach(r => {
        const flag = r.flags && (r.flags.svg || r.flags.png) ? (r.flags.svg || r.flags.png) : '';
        const cur = r.currencies || {};
        Object.keys(cur).forEach(code => {
          if (!code) return;
          if (!map[code]) {
            map[code] = { code, name: (cur[code] && cur[code].name) || code, flag };
          }
        });
      });
      const list = Object.values(map).sort((a, b) => a.code.localeCompare(b.code));
      setCurrencies(list);
    } catch (err) {
      console.error('fetchCurrencies error', err);
    } finally {
      setCurrenciesLoading(false);
    }
  };
   const navigate = useNavigate();
   const { t, currentLanguage } = useTranslation();
  const confirm = useConfirm();
  // Liczba sztuk jako tekst pola (jak kwoty, U.25): liczba powstaje dopiero w wyliczeniach.
  const [iloscTekst, setIloscTekst] = useState('1');
  const ilosc = iloscZPola(iloscTekst);
  const wieleSztuk = (ilosc || 1) > 1;
  const zmienIlosc = (o) => {
    const teraz = ilosc || 1;
    setIloscTekst(String(Math.min(MAKS_SZTUK, Math.max(1, teraz + o))));
    setFieldErrors(prev => ({ ...prev, quantity: false }));
  };
  // „Dodaj 2 sztuki”, „Dodaj 5 sztuk”: odmiana jak w kalendarzu (Intl.PluralRules).
  const liczbaSztuk = (n, rodzaj) => {
    const forma = new Intl.PluralRules(localeTag(currentLanguage)).select(n);
    if (rodzaj === 'saved') {
      if (forma === 'few') return t('addEquipment.savedManyFew').replace('{n}', String(n));
      if (forma === 'many') return t('addEquipment.savedManyMany').replace('{n}', String(n));
      return t('addEquipment.savedManyOther').replace('{n}', String(n));
    }
    if (forma === 'few') return t('addEquipment.saveManyFew').replace('{n}', String(n));
    if (forma === 'many') return t('addEquipment.saveManyMany').replace('{n}', String(n));
    return t('addEquipment.saveManyOther').replace('{n}', String(n));
  };

   useEffect(() => {
     axios.get(`${API_BASE}/api/classes`)
       .then(res => setClasses(res.data))
       .catch(() => setClasses([]));

    // podpowiedzi z istniejących rekordów (dla aktywnych pól tekstowych)
    axios.get(`${API_BASE}/api/equipment`, { withCredentials: true })
      .then(res => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const suggestionFields = [
          'classId',
          'brand',
          'location',
          'supplier'
        ];
        const sets = suggestionFields.reduce((acc, key) => {
          acc[key] = new Set();
          return acc;
        }, {});

        rows.forEach(row => {
          const meta = row && typeof row.metadata === 'object' && row.metadata ? row.metadata : {};
          suggestionFields.forEach(key => {
            const fromTop = key === 'classId'
              ? (row ? (row.class ?? row.class_name) : undefined)
              : (row ? row[key] : undefined);
            const fromMeta = meta[key];
            const value = fromTop ?? fromMeta;
            if (Array.isArray(value)) {
              value.forEach(v => {
                const s = String(v || '').trim();
                if (s) sets[key].add(s);
              });
            } else {
              const s = String(value ?? '').trim();
              if (s) sets[key].add(s);
            }
          });
        });

        const next = {};
        Object.keys(sets).forEach(key => {
          next[key] = Array.from(sets[key]).slice(0, 100);
        });
        setFieldSuggestionIndex(next);
      })
      .catch(() => setFieldSuggestionIndex({}));

    // pobierz kategorie bezpośrednio i przefiltruj lokalne ustawienia kategorii
    axios.get(`${API_BASE}/api/equipment-categories`, { withCredentials: true })
      .then(res => {
        const rows = res.data || [];
        const names = rows
          .map(r => String(r?.name || '').trim())
          .filter(Boolean);
        const persisted = loadSettings().categories || [];
        const mergedNames = Array.from(new Set([...(persisted || []), ...names]));
        setCategories(mergedNames);
        // ustaw też types fallback jeśli potrzebne
        setTypes(rows);

        // zachowaj lokalne wymagania i dopisz brakujące kategorie z pustą konfiguracją
        try {
          const currentSettings = loadSettings();
          const current = currentSettings.categoryRequirements || {};
          const nextRequirements = { ...current };
          mergedNames.forEach(name => {
            if (!Array.isArray(nextRequirements[name])) nextRequirements[name] = [];
          });
          setCategoryRequirements(nextRequirements);

          try {
            const settingsKey = getInventorySettingsStorageKey();
            const categoriesKey = getInventoryCategoriesStorageKey();
            const rawPrev = window.localStorage.getItem(settingsKey);
            const prev = rawPrev ? JSON.parse(rawPrev) : {};
            // `requiredFields` NIE jest tu ustawiane — `currentSettings.requiredFields` to
            // zawsze DOMYŚLNA, niepusta tablica (loadSettings() dopełnia braki defaultami),
            // a `shouldMigrateLocal` (inventorySettingsApi.js) traktuje samą OBECNOŚĆ klucza
            // requiredFields jako "użytkownik skonfigurował magazyn". Zapisanie tu defaultu
            // wystarczało, żeby samo otwarcie "Dodaj sprzęt" (bez wizyty w Ustawieniach
            // magazynu) auto-migrowało pustą konfigurację na serwer przy następnym starcie
            // aplikacji i checklista "Pierwsze kroki" fałszywie odhaczała ten krok.
            const next = {
              ...prev,
              categoryRequirements: nextRequirements,
              categories: mergedNames
            };
            window.localStorage.setItem(settingsKey, JSON.stringify(next));
            window.localStorage.setItem(categoriesKey, JSON.stringify(mergedNames));
          } catch(_) {}
        } catch (_) {}
      })
      .catch(() => {
        // fallback: pobierz types jak wcześniej
        const persisted = loadSettings().categories || [];
        setCategories(persisted);
        axios.get(`${API_BASE}/api/types`, { withCredentials: true })
          .then(res => {
            const rows = res.data || [];
            setTypes(rows);
            const fromTypes = rows.map(r => String(r?.name || '').trim()).filter(Boolean);
            const mergedNames = Array.from(new Set([...(persisted || []), ...fromTypes]));
            setCategories(mergedNames);
            try {
              window.localStorage.setItem(getInventoryCategoriesStorageKey(), JSON.stringify(mergedNames));
            } catch (_) {}
          })
          .catch(()=>setTypes([]));
      });
   }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const settingsKey = getInventorySettingsStorageKey();
    const onStorage = (e) => {
      if (e.key === settingsKey) {
        const next = loadSettings();
        setRequiredFields(next.requiredFields);
        setVisibleColumns(next.visibleColumns);
        setCategoryRequirements(next.categoryRequirements);
        setCategories(next.categories || []);
        setCustomColumns(next.customColumns || []);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Automatyczny sync brakujących kluczy tłumaczeń przy starcie strony
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const keys = translations && translations.pl ? Object.keys(translations.pl) : [];
      if (!keys.length) return;
      const chunkSize = 500;
      for (let i = 0; i < keys.length; i += chunkSize) {
        const chunk = keys.slice(i, i + chunkSize);
        // wysyłamy asynchronicznie, nie blokujemy rendering
        fetch(`${API_BASE}/api/i18n/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keys: chunk, lang: 'pl' }),
          credentials: 'include'
        }).catch(() => {});
      }
    } catch (_) {}
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setFieldErrors(prev => ({ ...prev, [name]: false }));
  };

  const closeTypeahead = () => setActiveTypeahead({ field: '', items: [] });

  const getTypeaheadValues = (field) => {
    if (field === 'classId') {
      return Array.from(new Set([
        ...((fieldSuggestionIndex.classId || []).map(v => String(v || '').trim()).filter(Boolean)),
        ...((classes || []).map(cls => String(cls?.name || '').trim()).filter(Boolean))
      ]));
    }
    return Array.from(new Set(((fieldSuggestionIndex[field] || []).map(v => String(v || '').trim()).filter(Boolean))));
  };

  const updateTypeahead = (field, value) => {
    const q = String(value || '').trim().toLowerCase();
    if (q.length < TYPEAHEAD_MIN_LENGTH) {
      closeTypeahead();
      return;
    }

    const matches = getTypeaheadValues(field)
      .filter(item => item.toLowerCase().startsWith(q))
      .slice(0, 8);

    setActiveTypeahead({ field, items: matches });
  };

  const handleTypeaheadChange = (e) => {
    handleChange(e);
    updateTypeahead(e.target.name, e.target.value);
  };

  const handleTypeaheadSelect = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => ({ ...prev, [field]: false }));
    closeTypeahead();
  };

  const renderTypeahead = (field) => (
    activeTypeahead.field === field && activeTypeahead.items.length > 0 ? (
      <TypeaheadList>
        {activeTypeahead.items.map(item => (
          <TypeaheadItem
            type="button"
            key={`${field}-${item}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleTypeaheadSelect(field, item)}
          >
            <DotIcon />
            <span>{item}</span>
          </TypeaheadItem>
        ))}
      </TypeaheadList>
    ) : null
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onMouseDown = (e) => {
      if (e.target?.closest?.('[data-equipment-typeahead="true"]')) return;
      closeTypeahead();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  // Które pole formularza przyjmuje skan. Liczone z tych samych zbiorów, którymi steruje
  // się widoczność pól niżej w JSX — żeby czytnik nie celował w pole, którego nie ma.
  const scanTargetField = (() => {
    const widoczne = new Set(
      [...baseFormFields, ...(requiredFields || []), ...baseRequiredFields]
        .map(normalizeActiveFieldKey)
        .filter(Boolean)
    );
    if (widoczne.has('barcode')) return 'barcode';
    if (widoczne.has('inventory_number')) return 'inventory_number';
    return null;
  })();

  // Czytnik USB, aparat i telefon wpisują kod w to samo pole (ScanSources pod polem kodu).
  const handleScanFill = (kod) => {
    if (!scanTargetField) return undefined;
    setForm(f => ({ ...f, [scanTargetField]: kod }));
    setFieldErrors(prev => ({ ...prev, [scanTargetField]: false }));
    setScannedInto(scanTargetField);
    return { tone: 'ok', text: t('addEquipment.scanFilled').replace('{code}', kod) };
  };
  // Przy kilku sztukach kod producenta wpisuje się w każdej sztuce osobno, więc skan do pola kodu
  // jest wtedy wyłączony (numer inwentarzowy pierwszej sztuki nadal da się zeskanować).
  useBarcodeScan(handleScanFill, { enabled: !!scanTargetField && !(wieleSztuk && scanTargetField === 'barcode') });

  useEffect(() => {
    if (!scannedInto) return undefined;
    const id = setTimeout(() => setScannedInto(null), 4000);
    return () => clearTimeout(id);
  }, [scannedInto]);

  // Czytnik kończy każdy kod Enterem. W polu wewnątrz formularza z przyciskiem
  // `type="submit"` Enter WYSYŁA formularz — więc zeskanowanie w połowie wypełniania
  // zapisywało niekompletny sprzęt albo wyrzucało „Uzupełnij wszystkie pola”.
  // Blokujemy Enter TYLKO w polach kodu; w pozostałych zostaje jako zwykłe ułatwienie.
  const blokujEnter = (e) => { if (e.key === 'Enter') e.preventDefault(); };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setFieldErrors({});
    
    const requiredErrors = {};
    const categoryKey = form.category || '';
    const categoryExtra = categoryRequirements?.[categoryKey] || [];
    const requiredSet = new Set(
      [...(requiredFields || []), ...baseRequiredFields, ...categoryExtra]
        .map(normalizeActiveFieldKey)
        .filter(Boolean)
    );
    if (requiredSet.has('name') && !form.name) requiredErrors.name = true;
    if (requiredSet.has('size') && !form.size) requiredErrors.size = true;
    if (requiredSet.has('brand') && !form.brand) requiredErrors.brand = true;
    if (requiredSet.has('price') && !form.price) requiredErrors.price = true;
    if (requiredSet.has('price_over_7') && !form.price_over_7) requiredErrors.price_over_7 = true;
    if (requiredSet.has('bail') && !form.bail) requiredErrors.bail = true;
    if (requiredSet.has('classId') && !form.classId) requiredErrors.classId = true;
    if (requiredSet.has('typeId') && !form.typeId) requiredErrors.typeId = true;
    if (requiredSet.has('inventory_number') && !form.inventory_number) requiredErrors.inventory_number = true;
    if (requiredSet.has('sku') && !form.sku) requiredErrors.sku = true;
    if (requiredSet.has('condition') && !form.condition) requiredErrors.condition = true;
    if (requiredSet.has('location') && !form.location) requiredErrors.location = true;
    if (requiredSet.has('tags') && !form.tags) requiredErrors.tags = true;
    if (requiredSet.has('supplier') && !form.supplier) requiredErrors.supplier = true;
    if (requiredSet.has('purchaseDate') && !form.purchaseDate) requiredErrors.purchaseDate = true;
    if (requiredSet.has('maintenanceDate') && !form.maintenanceDate) requiredErrors.maintenanceDate = true;
    if (requiredSet.has('images') && !form.images) requiredErrors.images = true;
    if (requiredSet.has('barcode') && !form.barcode) requiredErrors.barcode = true;
    if (requiredSet.has('currency') && !form.currency) requiredErrors.currency = true;
    if (requiredSet.has('wearLevel') && !form.wearLevel) requiredErrors.wearLevel = true;
    if (requiredSet.has('compatibility') && !form.compatibility) requiredErrors.compatibility = true;
    if (requiredSet.has('sizeRange') && !form.sizeRange) requiredErrors.sizeRange = true;

    // Pola własne — ta sama flaga `required`, która stawia gwiazdkę w szablonie importu
    // i odrzuca pusty wiersz w parserze (E.3). Bez tej pętli wymagalność pola własnego
    // działałaby TYLKO w imporcie, a ręczne dodanie omijałoby ją bez słowa.
    (customColumns || []).forEach((col) => {
      if (!requiredSet.has(col.key)) return;
      const wartosc = customValues[col.key];
      if (wartosc === undefined || wartosc === null || String(wartosc).trim() === '') {
        requiredErrors[col.key] = true;
      }
    });

    if (ilosc === null) requiredErrors.quantity = true;

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      setError(Object.keys(requiredErrors).length === 1 && requiredErrors.quantity
        ? t('addEquipment.quantityInvalid').replace('{max}', String(MAKS_SZTUK))
        : (t('addEquipment.fillAll') || 'Uzupełnij wszystkie pola!'));
      scrollToTop();
      return;
    }
    // jeżeli istnieje pole category w formularzu wymagamy wyboru
    if (requiredSet.has('category') && categories.length > 0 && !form.category) {
      setFieldErrors(prev => ({ ...prev, category: true }));
      setError(t('addEquipment.selectCategory') || 'Wybierz kategorię sprzętu!');
      scrollToTop();
      return;
    }
    
    setLoading(true);
    try {
      const parseList = (value) => String(value || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);

      const metadata = {
        ...(form.sku ? { sku: form.sku } : {}),
        ...(form.condition ? { condition: form.condition } : {}),
        ...(form.location ? { location: form.location } : {}),
        ...(form.tags ? { tags: parseList(form.tags) } : {}),
        ...(form.supplier ? { supplier: form.supplier } : {}),
        ...(form.purchaseDate ? { purchaseDate: form.purchaseDate } : {}),
        ...(form.maintenanceDate ? { maintenanceDate: form.maintenanceDate } : {}),
        ...(form.images ? { images: parseList(form.images) } : {}),
        ...(form.barcode && !wieleSztuk ? { barcode: form.barcode } : {}),
        ...(form.currency ? { currency: form.currency } : {}),
        ...(form.wearLevel ? { wearLevel: form.wearLevel } : {}),
        ...(form.compatibility ? { compatibility: form.compatibility } : {}),
        ...(form.sizeRange ? { sizeRange: form.sizeRange } : {})
      };

      // Pola własne lądują w tym samym `metadata`, z którego czyta je tabela magazynu
      // (`resolveInventoryValue`) i import (`MERGE` w routes/import.js). Puste pomijamy,
      // żeby nie zapisywać pustych łańcuchów tam, gdzie wcześniej nie było klucza.
      (customColumns || []).forEach((col) => {
        const wartosc = customValues[col.key];
        if (wartosc === undefined || wartosc === null || String(wartosc).trim() === '') return;
        metadata[col.key] = wartosc;
      });

      const normalizedClassValue = String(form.classId || '').trim();
      const matchedClass = (classes || []).find(cls => String(cls?.name || '').trim().toLowerCase() === normalizedClassValue.toLowerCase());

      const csrf = localStorage.getItem('csrfToken') || '';
      // Numer nieaktywnej sztuki: okno „Przenieść numer?” i ponowny zapis (utils/przeniesienieNumeru.js).
      await zapiszZPrzeniesieniemNumeru((dodatkowe) => axios.post(`${API_BASE}/api/equipment`, {
        ...dodatkowe,
        name: form.name,
        size: form.size || null,
        brand: form.brand || null,
        price: form.price,
        price_over_7: form.price_over_7,
        bail: form.bail,
        inventory_number: form.inventory_number, // <-- dodane pole
        classId: matchedClass ? matchedClass.id : normalizedClassValue,
        typeId: form.typeId,
        // dołącz kategorię (jeśli wybrana) — backend może ją zignorować jeśli nie potrzebuje
        category: form.category || null,
        metadata,
        // Kilka sztuk: każda dostaje swój numer z serii liczonej od numeru pierwszej sztuki.
        ...(wieleSztuk ? { quantity: ilosc, inventory_numbers: kolejneNumery(form.inventory_number, ilosc) } : {}),
      }, {
        withCredentials: true,  // Wysyła cookies (sid) z żądaniem
        headers: { 'X-CSRF-Token': csrf }
      }), { confirm, t });
      setSuccess(wieleSztuk ? liczbaSztuk(ilosc, 'saved') : 'Sprzęt został dodany pomyślnie!');
      // Przycisk zapisu jest na dole długiego formularza, a komunikat na górze: bez przewinięcia
      // użytkownik nie widział potwierdzenia przed powrotem do magazynu.
      scrollToTop();
      setTimeout(() => {
        navigate('/inventory');
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Błąd podczas dodawania sprzętu.';
      setError(msg);
      // Parse error to highlight fields
      const msgLower = msg.toLowerCase();
      const errors = {};
      if (/nazwa|name/.test(msgLower)) errors.name = true;
      if (/rozmiar|size/.test(msgLower)) errors.size = true;
      if (/marka|brand/.test(msgLower)) errors.brand = true;
      if (/(price_over_7|over[_\s-]?7|>\s*7|7\s*dni|powyżej\s*7)/.test(msgLower)) errors.price_over_7 = true;
      if (/(cena\s*za\s*dzień|price\s*per\s*day)/.test(msgLower)) errors.price = true;
      if (/cena|price/.test(msgLower) && !errors.price && !errors.price_over_7) errors.price = true;
      if (/kaucja|bail/.test(msgLower)) errors.bail = true;
      if (/numer|inventory/.test(msgLower)) errors.inventory_number = true;
      if (/kategoria|category/.test(msgLower)) errors.category = true;
      if (/klasa|class/.test(msgLower)) errors.classId = true;
      if (/typ|type/.test(msgLower)) errors.typeId = true;
      if (/sku|kod/.test(msgLower)) errors.sku = true;
      if (/stan|condition/.test(msgLower)) errors.condition = true;
      if (/lokalizacj|location/.test(msgLower)) errors.location = true;
      if (/tag/.test(msgLower)) errors.tags = true;
      if (/dostawca|supplier/.test(msgLower)) errors.supplier = true;
      if (/zakup|purchase/.test(msgLower)) errors.purchaseDate = true;
      if (/serwis|maintenance/.test(msgLower)) errors.maintenanceDate = true;
      if (/zdj|image/.test(msgLower)) errors.images = true;
      if (/barcode|qr|kresk/.test(msgLower)) errors.barcode = true;
      if (/walut|currency/.test(msgLower)) {
        errors.currency = true;
      }
      if (/zuzy|wear|usage/.test(msgLower)) errors.wearLevel = true;
      if (/kompat|compatib/.test(msgLower)) errors.compatibility = true;
      if (/zakres|range/.test(msgLower)) errors.sizeRange = true;
      setFieldErrors(errors);
      scrollToTop();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        {/* Wyjscie u GORY, ujednolicone z reszta podstron (minimalistyczna strzalka zamiast
            wielkiego przycisku tekstowego). „Anuluj" zostaje TAKZE na dole przy formularzu, zeby
            na telefonie nie trzeba bylo przewijac calego formularza z powrotem do gory. */}
        <IconButton onClick={() => navigate('/inventory')} aria-label={t('addEquipment.backToInventory')}>
          <ArrowLeftIcon />
        </IconButton>
        <TitleSection>
          <Title>
            <TitleIcon><PlusIcon /></TitleIcon>
            {t('addEquipment.title') || 'Dodaj nowy sprzęt'}
          </Title>
          <Subtitle>{t('addEquipment.subtitle') || 'Wypełnij formularz, aby dodać nowy sprzęt do inwentarza'}</Subtitle>
        </TitleSection>
      </Header>
        <TylkoOdczytWPrzerwie>

      {error && (
        <div style={{ marginBottom: 20 }}><Banner tone="danger">{error}</Banner></div>
      )}

      {success && (
        <div style={{ marginBottom: 20 }}><Banner tone="success">{success}</Banner></div>
      )}

      <FormPanel>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            {/* Toolbar: kontrola pól opcjonalnych zależnych od kategorii */}
            {(() => {
              const categoryKey = form.typeId ? (types.find(t => String(t.id) === String(form.typeId))?.name || form.category) : form.category;
              const categoryExtra = categoryRequirements?.[categoryKey] || [];
              // E.7: WIDOCZNOŚĆ pola to co innego niż jego WYMAGALNOŚĆ. `baseFormFields` to
              // pola, które formularz oferuje zawsze (np. marka — opcjonalna od E.5, ale
              // nadal do wpisania); `requiredFields`/`baseRequiredFields` decydują wyłącznie
              // o tym, czy są obowiązkowe. Wyprowadzanie widoczności z wymagalności sprawiało,
              // że pole znikało z formularza w chwili, gdy przestawało być wymagane.
              const visibleSet = new Set(
                [...baseFormFields, ...(requiredFields || []), ...baseRequiredFields, ...categoryExtra, 'typeId',
                  ...(visibleColumns || []).filter(k => k === 'size' || k === 'class')]
                  .map(normalizeActiveFieldKey)
                  .filter(Boolean)
              );
              const allKeys = Object.keys(form).filter(k => k !== 'category');
              const should = (key) => visibleSet.has(key);
              return (
                <>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: '0.95rem', color: 'var(--text-muted)' }}>
                      <strong>Pola aktywne dla wybranej kategorii: </strong>
                      {Array.from(visibleSet).length}/{allKeys.length}
                    </div>
                  </div>

                  {should('typeId') && (
                    <FormRow>
                      <Label>{t('addEquipment.type') || 'Kategoria sprzętu'}</Label>
                      <FilterDropdown
                        value={form.typeId}
                        onChange={value => {
                          const sel = types.find(tt => String(tt.id) === String(value));
                          setForm(f => ({ ...f, typeId: value, category: sel ? sel.name : f.category }));
                          setFieldErrors(prev => ({ ...prev, typeId: false, category: false }));
                        }}
                        placeholder={t('addEquipment.typePlaceholder') || 'Wybierz kategorię'}
                        error={!!fieldErrors.typeId || !!fieldErrors.category}
                        dataCy="add-equipment-type"
                        options={[
                          ...types.map(type => {
                            return {
                              value: type.id,
                              label: typeLabel(t, type.name)
                            };
                          })
                        ]}
                      />
                    </FormRow>
                  )}

                  {should('brand') && (
                    <FormRow>
                      <Label>{t('addEquipment.brand') || 'Marka'}</Label>
                      <TypeaheadWrapper data-equipment-typeahead="true">
                        <Input
                          name="brand"
                          value={form.brand}
                          autoComplete="off"
                          onChange={handleTypeaheadChange}
                          onFocus={(e) => updateTypeahead('brand', e.target.value)}
                          placeholder={t('addEquipment.brandPlaceholder') || 'np. Rossignol'}
                          data-error={!!fieldErrors.brand}
                          data-cy="add-equipment-brand"
                        />
                        {renderTypeahead('brand')}
                      </TypeaheadWrapper>
                    </FormRow>
                  )}

                  {should('name') && (
                    <FormRow>
                      <Label>{t('addEquipment.name') || 'Nazwa modelu'}</Label>
                      <Input
                        name="name"
                        value={form.name}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.namePlaceholder') || 'np. Hero Elite ST'}
                        data-error={!!fieldErrors.name}
                        data-cy="add-equipment-name"
                      />
                    </FormRow>
                  )}

                  {should('size') && (
                    <FormRow>
                      <Label>{t('addEquipment.size') || 'Rozmiar'}</Label>
                      <Input
                        name="size"
                        value={form.size}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.sizePlaceholder') || 'np. 170cm'}
                        data-error={!!fieldErrors.size}
                        data-cy="add-equipment-size"
                      />
                    </FormRow>
                  )}

                  {should('inventory_number') && (
                    <FormRow>
                      <Label>{wieleSztuk ? t('addEquipment.inventoryNumberFirst') : (t('addEquipment.inventoryNumber') || 'Numer')}</Label>
                      <Input
                        name="inventory_number"
                        value={form.inventory_number}
                        autoComplete="off"
                        onChange={handleChange}
                        onKeyDown={blokujEnter}
                        placeholder={t('addEquipment.inventoryNumberPlaceholder') || 'np. N-ROS-175-01'}
                        data-error={!!fieldErrors.inventory_number}
                        data-cy="add-equipment-number"
                      />
                      {scanTargetField === 'inventory_number' && (
                        <>
                          <ScanHint data-cy="add-equipment-scan-hint" $done={scannedInto === 'inventory_number'}>
                            {scannedInto === 'inventory_number' ? t('addEquipment.scanDone') : t('addEquipment.scanHint')}
                          </ScanHint>
                          <ScanSources onScan={handleScanFill} compact dataCyPrefix="add-equipment-scan" />
                        </>
                      )}
                    </FormRow>
                  )}

                  {should('classId') && (
                    <FormRow>
                      <Label>{t('addEquipment.class') || 'Klasa sprzętu'}</Label>
                      <TypeaheadWrapper data-equipment-typeahead="true">
                        <Input
                          name="classId"
                          value={form.classId}
                          autoComplete="off"
                          onChange={handleTypeaheadChange}
                          onFocus={(e) => updateTypeahead('classId', e.target.value)}
                          placeholder={t('addEquipment.classPlaceholder') || 'Wpisz klasę sprzętu'}
                          data-error={!!fieldErrors.classId}
                          data-cy="add-equipment-class"
                        />
                        {renderTypeahead('classId')}
                      </TypeaheadWrapper>
                    </FormRow>
                  )}

                  {should('price') && (
                    <FormRow>
                      <Label>{t('addEquipment.price') || 'Cena za dzień (PLN)'}</Label>
                      <Input 
                        name="price" 
                        type="number" 
                        min="0" 
                        step="0.01" 
                        value={form.price} 
                        onChange={handleChange} 
                        onWheel={e => e.target.blur()}
                        placeholder={t('addEquipment.pricePlaceholder') || '50.00'}
                        data-error={!!fieldErrors.price}
                        data-cy="add-equipment-price"
                      />
                    </FormRow>
                  )}

                  {should('price_over_7') && (
                    <FormRow>
                      <Label>{t('addEquipment.priceOver7') || 'Cena za dzień (> 7 dni)'}</Label>
                      <Input
                        name="price_over_7"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.price_over_7}
                        onChange={handleChange}
                        onWheel={e => e.target.blur()}
                        placeholder={t('addEquipment.priceOver7Placeholder') || '45.00'}
                        data-error={!!fieldErrors.price_over_7}
                        data-cy="add-equipment-price-over-7"
                      />
                    </FormRow>
                  )}

                  {should('bail') && (
                    <FormRow>
                      <Label>{t('addEquipment.bail') || 'Kaucja (PLN)'}</Label>
                      <Input 
                        name="bail" 
                        type="number" 
                        min="0" 
                        step="0.01" 
                        value={form.bail} 
                        onChange={handleChange} 
                        onWheel={e => e.target.blur()}
                        placeholder={t('addEquipment.bailPlaceholder') || '200.00'}
                        data-error={!!fieldErrors.bail}
                        data-cy="add-equipment-bail"
                      />
                    </FormRow>
                  )}

                  {should('sku') && (
                    <FormRow>
                      <Label>{t('addEquipment.sku') || 'Kod/SKU'}</Label>
                      <Input
                        name="sku"
                        value={form.sku}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.skuPlaceholder') || 'np. SKU-NS-001'}
                        data-error={!!fieldErrors.sku}
                        data-cy="add-equipment-sku"
                      />
                    </FormRow>
                  )}

                  {should('condition') && (
                    <FormRow>
                      <Label>{t('addEquipment.condition') || 'Stan'}</Label>
                      <Input
                        name="condition"
                        value={form.condition}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.conditionPlaceholder') || 'np. Nowy / Uzywany / Serwis'}
                        data-error={!!fieldErrors.condition}
                        data-cy="add-equipment-condition"
                      />
                    </FormRow>
                  )}

                  {should('location') && (
                    <FormRow>
                      <Label>{t('addEquipment.location') || 'Lokalizacja'}</Label>
                      <TypeaheadWrapper data-equipment-typeahead="true">
                        <Input
                          name="location"
                          value={form.location}
                          autoComplete="off"
                          onChange={handleTypeaheadChange}
                          onFocus={(e) => updateTypeahead('location', e.target.value)}
                          placeholder={t('addEquipment.locationPlaceholder') || 'np. Regal B2'}
                          data-error={!!fieldErrors.location}
                          data-cy="add-equipment-location"
                        />
                        {renderTypeahead('location')}
                      </TypeaheadWrapper>
                    </FormRow>
                  )}

                  {should('tags') && (
                    <FormRow>
                      <Label>{t('addEquipment.tags') || 'Tagi'}</Label>
                      <Input
                        name="tags"
                        value={form.tags}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.tagsPlaceholder') || 'np. dzieciecy, promo'}
                        data-error={!!fieldErrors.tags}
                        data-cy="add-equipment-tags"
                      />
                    </FormRow>
                  )}

                  {should('supplier') && (
                    <FormRow>
                      <Label>{t('addEquipment.supplier') || 'Dostawca'}</Label>
                      <TypeaheadWrapper data-equipment-typeahead="true">
                        <Input
                          name="supplier"
                          value={form.supplier}
                          autoComplete="off"
                          onChange={handleTypeaheadChange}
                          onFocus={(e) => updateTypeahead('supplier', e.target.value)}
                          placeholder={t('addEquipment.supplierPlaceholder') || 'np. WinterSport Sp. z o.o.'}
                          data-error={!!fieldErrors.supplier}
                          data-cy="add-equipment-supplier"
                        />
                        {renderTypeahead('supplier')}
                      </TypeaheadWrapper>
                    </FormRow>
                  )}

                  {should('purchaseDate') && (
                    <FormRow>
                      <Label>{t('addEquipment.purchaseDate') || 'Data zakupu'}</Label>
                      <div style={{ position: 'relative' }}>
                        <Input
                          ref={purchaseDateRef}
                          name="purchaseDate"
                          type="date"
                          value={form.purchaseDate}
                          placeholder={t('addEquipment.purchaseDate') || 'Data zakupu'}
                          aria-label={t('addEquipment.purchaseDate') || 'Data zakupu'}
                          title={t('addEquipment.purchaseDate') || 'Data zakupu'}
                          onChange={handleChange}
                          data-error={!!fieldErrors.purchaseDate}
                          data-cy="add-equipment-purchase-date"
                        />
                        {!form.purchaseDate && (
                          <div
                            onClick={() => purchaseDateRef.current && purchaseDateRef.current.focus()}
                            style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'auto', cursor: 'text', fontSize: '0.95rem' }}
                          >
                            {t('addEquipment.purchaseDate') || 'Data zakupu'}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 6 }}>
                        {t('addEquipment.purchaseDate') || 'Data zakupu'}
                      </div>
                    </FormRow>
                  )}

                  {should('maintenanceDate') && (
                    <FormRow>
                      <Label>{t('addEquipment.maintenanceDate') || 'Data serwisu'}</Label>
                      <div style={{ position: 'relative' }}>
                        <Input
                          ref={maintenanceDateRef}
                          name="maintenanceDate"
                          type="date"
                          value={form.maintenanceDate}
                          placeholder={t('addEquipment.maintenanceDate') || 'Data serwisu'}
                          aria-label={t('addEquipment.maintenanceDate') || 'Data serwisu'}
                          title={t('addEquipment.maintenanceDate') || 'Data serwisu'}
                          onChange={handleChange}
                          data-error={!!fieldErrors.maintenanceDate}
                          data-cy="add-equipment-maintenance-date"
                        />
                        {!form.maintenanceDate && (
                          <div
                            onClick={() => maintenanceDateRef.current && maintenanceDateRef.current.focus()}
                            style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'auto', cursor: 'text', fontSize: '0.95rem' }}
                          >
                            {t('addEquipment.maintenanceDate') || 'Data serwisu'}
                          </div>
                        )}
                      </div>
                    </FormRow>
                  )}

                  {should('images') && (
                    <FormRow>
                      <Label>{t('addEquipment.images') || 'Zdjecia (URL)'}</Label>
                      <Input
                        name="images"
                        value={form.images}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.imagesPlaceholder') || 'np. https://... , https://...'}
                        data-error={!!fieldErrors.images}
                        data-cy="add-equipment-images"
                      />
                    </FormRow>
                  )}

                  {should('barcode') && (
                    <FormRow>
                      <Label>{t('addEquipment.barcode') || 'Kod kreskowy/QR'}</Label>
                      <Input
                        name="barcode"
                        value={form.barcode}
                        autoComplete="off"
                        onChange={handleChange}
                        onKeyDown={blokujEnter}
                        placeholder={t('addEquipment.barcodePlaceholder') || 'np. 5901234567890'}
                        data-error={!!fieldErrors.barcode}
                        data-cy="add-equipment-barcode"
                        disabled={wieleSztuk}
                      />
                      {wieleSztuk && <ScanHint data-cy="add-equipment-barcode-per-unit">{t('addEquipment.barcodePerUnit')}</ScanHint>}
                      {scanTargetField === 'barcode' && !wieleSztuk && (
                        <>
                          <ScanHint data-cy="add-equipment-scan-hint" $done={scannedInto === 'barcode'}>
                            {scannedInto === 'barcode' ? t('addEquipment.scanDone') : t('addEquipment.scanHint')}
                          </ScanHint>
                          <ScanSources onScan={handleScanFill} compact dataCyPrefix="add-equipment-scan" />
                        </>
                      )}
                    </FormRow>
                  )}

                  {should('currency') && (
                    <FormRow>
                      <Label>{t('addEquipment.currency') || 'Waluta'}</Label>
                      <div style={{ position: 'relative' }} ref={currencyContainerRef}>
                        <Input
                          name="currency"
                          autoComplete="off"
                          value={form.currency}
                          onChange={e => {
                            handleChange(e);
                            const q = String(e.target.value || '').trim().toUpperCase();
                            if (!q) {
                              setCurrencySuggestions([]);
                              setShowCurrencySuggestions(false);
                              return;
                            }
                            if (!currencies.length) fetchCurrencies();
                            const list = (currencies || []).filter(c => c.code.startsWith(q) || c.name.toLowerCase().includes(q.toLowerCase()));
                            setCurrencySuggestions(list.slice(0, 12));
                            setShowCurrencySuggestions(true);
                          }}
                          onFocus={() => {
                            if (!currencies.length) fetchCurrencies();
                            const q = String(form.currency || '').trim().toUpperCase();
                            const list = (currencies || []).filter(c => !q || c.code.startsWith(q) || c.name.toLowerCase().includes(q.toLowerCase()));
                            setCurrencySuggestions(list.slice(0, 12));
                            setShowCurrencySuggestions(true);
                          }}
                          placeholder={t('addEquipment.currencyPlaceholder') || 'np. PLN'}
                          data-error={!!fieldErrors.currency}
                          data-cy="add-equipment-currency"
                        />

                        {showCurrencySuggestions && (currencySuggestions.length > 0 || currenciesLoading) && (
                          <div style={{
                            position: 'absolute',
                            zIndex: 'var(--z-dropdown)',
                            left: 0,
                            right: 0,
                            background: 'var(--card-bg)',
                            border: '1px solid var(--input-border)',
                            boxShadow: 'var(--shadow-md)',
                            maxHeight: 260,
                            overflow: 'auto'
                          }}>
                            {currenciesLoading && <div style={{ padding: 8, color: 'var(--text-muted)' }}>{t('addEquipment.currencyLoading')}</div>}
                            {!currenciesLoading && currencySuggestions.map(c => (
                              <div key={c.code} onMouseDown={(ev) => { ev.preventDefault(); }} onClick={() => {
                                setForm(prev => ({ ...prev, currency: c.code }));
                                setShowCurrencySuggestions(false);
                                setCurrencySuggestions([]);
                                setFieldErrors(prev => ({ ...prev, currency: false }));
                              }} style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', cursor: 'pointer' }}>
                                {c.flag && <img src={c.flag} alt={c.code} style={{ width: 20, height: 14, objectFit: 'cover', marginRight: 8 }} />}
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <div style={{ fontWeight: 600 }}>{c.code}</div>
                                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.name}</div>
                                </div>
                              </div>
                            ))}
                            {!currenciesLoading && currencySuggestions.length === 0 && (
                              <div style={{ padding: 8, color: 'var(--text-muted)' }}>{t('addEquipment.currencyNoMatches')}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </FormRow>
                  )}

                  {should('wearLevel') && (
                    <FormRow>
                      <Label>{t('addEquipment.wearLevel') || 'Poziom zuzycia'}</Label>
                      <Input
                        name="wearLevel"
                        value={form.wearLevel}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.wearLevelPlaceholder') || 'np. 20%'}
                        data-error={!!fieldErrors.wearLevel}
                        data-cy="add-equipment-wear-level"
                      />
                    </FormRow>
                  )}

                  {should('compatibility') && (
                    <FormRow>
                      <Label>{t('addEquipment.compatibility') || 'Kompatybilnosc'}</Label>
                      <Input
                        name="compatibility"
                        value={form.compatibility}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.compatibilityPlaceholder') || 'np. Wiazania NN 75'}
                        data-error={!!fieldErrors.compatibility}
                        data-cy="add-equipment-compatibility"
                      />
                    </FormRow>
                  )}

                  {should('sizeRange') && (
                    <FormRow>
                      <Label>{t('addEquipment.sizeRange') || 'Zakres rozmiarow'}</Label>
                      <Input
                        name="sizeRange"
                        value={form.sizeRange}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder={t('addEquipment.sizeRangePlaceholder') || 'np. 36-42'}
                        data-error={!!fieldErrors.sizeRange}
                        data-cy="add-equipment-size-range"
                      />
                    </FormRow>
                  )}

                  {/* Pola własne z Ustawień magazynu (E.2). Renderują się WSZYSTKIE,
                      które organizacja zdefiniowała — skoro ktoś założył takie pole, chce je
                      wypełniać. Wymagalność to osobna sprawa (lekcja z E.7: widoczność pola
                      ≠ jego wymagalność) i rozstrzyga ją `requiredFields`. Kontrolka bierze
                      się z `fieldType`, tak samo jak w szablonie importu i w parserze. */}
                  {(customColumns || []).map((col) => {
                    // `requiredSet` żyje w handleSubmit; tutaj wystarczy sama lista — klucze pól
                    // własnych nie przechodzą przez `normalizeActiveFieldKey` (mapa zna tylko aliasy
                    // kolumn wbudowanych) i nigdy nie trafiają do wymagań per kategoria.
                    const wymagane = (requiredFields || []).includes(col.key);
                    const wartosc = customValues[col.key] ?? '';
                    const ustaw = (v) => {
                      setCustomValues((prev) => ({ ...prev, [col.key]: v }));
                      setFieldErrors((prev) => ({ ...prev, [col.key]: false }));
                    };
                    const etykieta = col.label || col.key;
                    return (
                      <FormRow key={col.key}>
                        <Label>{etykieta}{wymagane ? ' *' : ''}</Label>
                        {col.fieldType === 'list' ? (
                          <FilterDropdown
                            value={wartosc}
                            onChange={ustaw}
                            placeholder={col.example || etykieta}
                            error={!!fieldErrors[col.key]}
                            dataCy={`add-equipment-custom-${col.key}`}
                            options={(col.options || []).map((o) => ({ value: o, label: o }))}
                          />
                        ) : (
                          <Input
                            type={col.fieldType === 'number' ? 'number' : (col.fieldType === 'date' ? 'date' : 'text')}
                            value={wartosc}
                            autoComplete="off"
                            onChange={(e) => ustaw(e.target.value)}
                            placeholder={col.example || ''}
                            data-error={!!fieldErrors[col.key]}
                            data-cy={`add-equipment-custom-${col.key}`}
                            onWheel={col.fieldType === 'number' ? (e) => e.currentTarget.blur() : undefined}
                          />
                        )}
                      </FormRow>
                    );
                  })}

                  {/* Kilka jednakowych sztuk jednym zapisem (24.10.2026). Każda sztuka to osobna
                      pozycja w magazynie (osobne wypożyczenia, stan, etykieta), więc numery dostają
                      kolejne wartości liczone od numeru pierwszej sztuki. */}
                  <FormRowFull>
                    <QuantityPanel data-cy="add-equipment-quantity">
                      <QuantityField>
                        <Label htmlFor="add-equipment-quantity-input">{t('addEquipment.quantity')}</Label>
                        <Stepper $error={!!fieldErrors.quantity}>
                          <StepButton
                            type="button"
                            onClick={() => zmienIlosc(-1)}
                            disabled={(ilosc || 1) <= 1}
                            aria-label={t('addEquipment.quantityLess')}
                            data-cy="add-equipment-quantity-less"
                          >−</StepButton>
                          <StepInput
                            id="add-equipment-quantity-input"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={3}
                            value={iloscTekst}
                            onChange={(e) => { setIloscTekst(e.target.value.replace(/[^\d]/g, '')); setFieldErrors(prev => ({ ...prev, quantity: false })); }}
                            onBlur={() => { if (ilosc === null && iloscTekst.trim() === '') setIloscTekst('1'); }}
                            onKeyDown={blokujEnter}
                            aria-invalid={!!fieldErrors.quantity || undefined}
                            data-cy="add-equipment-quantity-input"
                          />
                          <StepButton
                            type="button"
                            onClick={() => zmienIlosc(1)}
                            disabled={(ilosc || 1) >= MAKS_SZTUK}
                            aria-label={t('addEquipment.quantityMore')}
                            data-cy="add-equipment-quantity-more"
                          >+</StepButton>
                        </Stepper>
                      </QuantityField>
                      <QuantityInfo aria-live="polite" data-cy="add-equipment-quantity-info">
                        {ilosc === null ? (
                          <span style={{ color: 'var(--danger)' }}>{t('addEquipment.quantityInvalid').replace('{max}', String(MAKS_SZTUK))}</span>
                        ) : !wieleSztuk ? (
                          <span>{t('addEquipment.quantityHintOne')}</span>
                        ) : (() => {
                          const numery = kolejneNumery(form.inventory_number, ilosc);
                          const skrot = skrotSerii(numery);
                          return (
                            <>
                              <span><strong>{t('addEquipment.quantityHintMany').replace('{n}', String(ilosc))}</strong></span>
                              {should('inventory_number') && numery[0] ? (
                                <SeriesPreview data-cy="add-equipment-quantity-numbers">
                                  {skrot.poczatek.map((n) => <code key={n}>{n}</code>)}
                                  {skrot.pominieto > 0 && <span aria-hidden="true">…</span>}
                                  {skrot.ostatni && <code>{skrot.ostatni}</code>}
                                </SeriesPreview>
                              ) : (
                                <span>{t('addEquipment.quantityNoNumbers')}</span>
                              )}
                            </>
                          );
                        })()}
                      </QuantityInfo>
                    </QuantityPanel>
                  </FormRowFull>
                </>
              );
            })()}

              </FormGrid>

            <ButtonRow>
              <PrimaryButton type="submit" disabled={loading} data-cy="add-equipment-save">
                <SaveIcon /> {loading
                  ? (t('addEquipment.saving') || 'Trwa zapisywanie...')
                  : (wieleSztuk ? liczbaSztuk(ilosc, 'save') : (t('addEquipment.save') || 'Dodaj sprzęt'))}
              </PrimaryButton>
              <SecondaryButton type="button" onClick={() => navigate('/inventory')}>
                <XIcon size={16} /> {t('addEquipment.cancel') || 'Anuluj'}
              </SecondaryButton>
            </ButtonRow>
          </form>
        </FormPanel>
        </TylkoOdczytWPrzerwie>
      </Container>
    );
}